'use client';

import { useEffect, useRef } from 'react';
import videojs from 'video.js';
import {
    createBtn,
    createDiv,
    createLeftControls,
    createOtherPartsElemrnts,
    createRightControls
} from './create-elements';
import farsiLanguage from './fa';
import { backwards, closeMenus, forwards, initQualityLevels, sendWatchToServer, togglePause } from './functions';

import { useKeepAwake } from '@insta-club/hooks';
import 'video.js/dist/video-js.css';
import { VideoProtection } from '../video-protection';
import './video.css';
import './videojs-landscape-fullscreen';

videojs.addLanguage('fa', farsiLanguage);

const FullVideoPlayer = ({
    hlsUrl,
    simpleURL,
    seasons,
    isFullHeight = false,
    episodeID,
    duration = 0,
    onSendWatchToServer,
    enableProtection = false,
    watermarkText = ''
}) => {
    useKeepAwake();
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const btnBigPlayPause = useRef(null);
    const btnPlayPause = useRef(null);
    const btnMute = useRef(null);
    const btnFullScreen = useRef(null);
    const otherParts = useRef(null);
    const seekBarRef = useRef(null);
    const currentTimeDisplayRef = useRef(null);
    const durationTimeDisplayRef = useRef(null);
    const observedTime = useRef(0);
    const hasSeasons = seasons && seasons.length > 0;
    const hasHls = !!hlsUrl;

    const options = {
        language: 'fa',
        controls: false,
        responsive: true,
        fluid: true,
        autoplay: true,
        textTrackSettings: true,
        sources: [
            {
                src: hlsUrl || simpleURL
            }
        ],
        userActions: {
            hotkeys: function (event) {
                if (event.which === 32) {
                    if (this?.paused()) {
                        this?.play();
                    } else {
                        this?.pause();
                    }
                }
                if (event.which === 70) {
                    if (this.isFullscreen()) {
                        this?.exitFullscreen();
                    } else {
                        this.requestFullscreen();
                    }
                }
                if (event.which === 77) {
                    this.muted(!this.muted());
                }
                if (event.which === 37 || event.which === 177) {
                    backwards(this)();
                }
                if (event.which === 39 || event.which === 176) {
                    forwards(this)();
                }
                if (event.which === 38 && this.volume() != 1) {
                    this.volume(parseFloat(this.volume() + 0.1).toFixed(1));
                }
                if (event.which === 40 && this.volume() != 0) {
                    this.volume(parseFloat(this.volume() - 0.1).toFixed(1));
                }
            }
        }
    };

    useEffect(() => {
        document.addEventListener('click', closeMenus);

        // Add fullscreen change listener for mobile devices
        const handleFullscreenChange = () => {
            if (enableProtection && watermarkText) {
                const watermarkContainer = document.getElementById('videojs-watermark-container');
                const isFullscreen =
                    document.fullscreenElement ||
                    document.webkitFullscreenElement ||
                    document.mozFullScreenElement ||
                    document.msFullscreenElement;

                if (watermarkContainer) {
                    if (isFullscreen) {
                        // Force watermark to be visible in fullscreen
                        watermarkContainer.style.position = 'fixed';
                        watermarkContainer.style.top = '0';
                        watermarkContainer.style.left = '0';
                        watermarkContainer.style.right = '0';
                        watermarkContainer.style.bottom = '0';
                        watermarkContainer.style.width = '100vw';
                        watermarkContainer.style.height = '100vh';
                        watermarkContainer.style.zIndex = '2147483647';
                        watermarkContainer.style.pointerEvents = 'none';
                        watermarkContainer.style.display = 'block';
                    } else {
                        // Reset watermark position when exiting fullscreen
                        watermarkContainer.style.position = 'absolute';
                        watermarkContainer.style.top = '0';
                        watermarkContainer.style.left = '0';
                        watermarkContainer.style.right = '0';
                        watermarkContainer.style.bottom = '0';
                        watermarkContainer.style.width = '';
                        watermarkContainer.style.height = '';
                        watermarkContainer.style.zIndex = '9999';
                        watermarkContainer.style.pointerEvents = 'none';
                        watermarkContainer.style.display = 'block';
                    }
                }
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        if (!playerRef.current) {
            const videoElement = document.createElement('video-js');
            videoElement.className = `vjs-theme-oxymovie ${isFullHeight ? 'height-full' : ''}`;
            videoElement.classList.add('vjs-big-play-centered');
            videoRef.current.appendChild(videoElement);
            const player = (playerRef.current = videojs(videoElement, options, () => {
                onReady(player);
                var originalSrc = player.src;
                player.src = function (e) {
                    if (e) {
                        var ret = originalSrc.call(player, e);
                        return ret;
                    }
                    return originalSrc.call(player);
                };
                player.on('play', onPlay);
                player.on('pause', onPause);
                player.on('fullscreenchange', onFullscreenchange(player));
                player.on('timeupdate', onTimeupdate(player));
                player.on('loadedmetadata', onLoadedmetadata(player));
            }));
            if (window && window.screen && player) {
                player.landscapeFullscreen(window.screen, {
                    fullscreen: {
                        enterOnRotate: true,
                        exitOnRotate: true,
                        alwaysInLandscapeMode: true,
                        iOS: true
                    }
                });
            }
        } else {
            const player = playerRef.current;
            player.autoplay(options.autoplay);
            player.src(options.sources);
        }

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, [videoRef]);

    useEffect(() => {
        return () => {
            cleanUp();
            document.removeEventListener('click', closeMenus);
        };
    }, [playerRef]);

    const onReady = (player) => {
        initQualityLevels(player);
        document.querySelector('.vjs-control-bar').remove();

        const controlBarWrapper = createDiv('control-bar-wrapper');

        const bigPlayButton = createDiv('oxy-big-play-button', 'oxy-big-play-button');
        const bottomBar = createDiv('bottom-bar');
        const nextTimeBar = createDiv('oxy-next-time-bar');

        btnBigPlayPause.current = createBtn('oxy-big-play-pause', 'fi fi-rr-play', togglePause(player));
        bigPlayButton.appendChild(btnBigPlayPause.current);

        const controls = createDiv('controls');
        const right = createRightControls(player, btnFullScreen, btnMute, otherParts, hasSeasons);
        const left = createLeftControls(player, btnPlayPause, currentTimeDisplayRef, durationTimeDisplayRef);
        controls.appendChild(left);
        controls.appendChild(right);

        if (seekBarRef.current == null) {
            const seekBarComponent = videojs.getComponent('ProgressControl');
            seekBarRef.current = new seekBarComponent(player);
            bottomBar.appendChild(seekBarRef.current.el());
        }

        bottomBar.appendChild(controls);

        controlBarWrapper.appendChild(bigPlayButton);
        controlBarWrapper.appendChild(bottomBar);

        player.el().appendChild(controlBarWrapper);
        player.el().appendChild(nextTimeBar);

        if (hasSeasons) {
            otherParts.current = createDiv('oxy-other-parts');
            const otherPartsElemrnts = createOtherPartsElemrnts(
                otherParts,
                seasons.filter((x) => x.episodes.length > 0),
                changeEpisode,
                hlsUrl || simpleURL
            );
            otherParts.current.appendChild(otherPartsElemrnts);
            player.el().appendChild(otherParts.current);
        }

        if (enableProtection && watermarkText) {
            const watermarkContainer = createDiv('video-watermark-container');
            watermarkContainer.id = 'videojs-watermark-container';
            watermarkContainer.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                pointer-events: none;
                z-index: 9999;
            `;

            const movingWatermark = createDiv('video-watermark-moving');
            movingWatermark.textContent = watermarkText;
            movingWatermark.style.cssText = `
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                font-size: 1rem;
                font-weight: bold;
                opacity: 0.5;
                pointer-events: none;
                z-index: 2147483647;
                text-shadow: 2px 2px 6px rgba(0,0,0,0.9);
                white-space: nowrap;
                transition: all 2s ease-in-out;
                direction: rtl;
                padding: 4px 8px;
                background-color: rgba(0,0,0,0.2);
                border-radius: 4px;
            `;
            watermarkContainer.appendChild(movingWatermark);

            const gridWatermark = createDiv('video-watermark-grid');
            gridWatermark.style.cssText = `
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                pointer-events: none;
                z-index: 2147483646;
                opacity: 0.2;
                display: grid;
                grid-template-columns: repeat(3, 1fr);
                grid-template-rows: repeat(4, 1fr);
                gap: 1rem;
                padding: 2rem;
            `;

            for (let i = 0; i < 12; i++) {
                const item = createDiv();
                item.textContent = watermarkText;
                item.style.cssText = `
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 0.75rem;
                    font-weight: bold;
                    transform: rotate(-25deg);
                    text-shadow: 0 0 8px rgba(0,0,0,0.6);
                    white-space: nowrap;
                    direction: rtl;
                `;
                gridWatermark.appendChild(item);
            }
            watermarkContainer.appendChild(gridWatermark);

            player.el().appendChild(watermarkContainer);

            const moveWatermark = () => {
                const x = Math.random() * 80 + 10;
                const y = Math.random() * 80 + 10;
                movingWatermark.style.top = `${y}%`;
                movingWatermark.style.left = `${x}%`;
            };
            setInterval(moveWatermark, 5000);
        }
    };

    const onPlay = () => {
        btnPlayPause.current.innerHTML = "<i class='fi fi-rr-pause'></i>";
        btnBigPlayPause.current.innerHTML = "<i class='fi fi-rr-pause'></i>";
    };

    const onPause = () => {
        btnPlayPause.current.innerHTML = "<i class='fi fi-rr-play'></i>";
        btnBigPlayPause.current.innerHTML = "<i class='fi fi-rr-play'></i>";
    };

    const onFullscreenchange = (player) => () => {
        if (btnFullScreen.current) {
            btnFullScreen.current.innerHTML = player.isFullscreen()
                ? "<i class='fi fi-rr-compress'></i>"
                : "<i class='fi fi-rr-expand'></i>";
        }

        // Handle watermark visibility in fullscreen for mobile
        if (enableProtection && watermarkText) {
            const watermarkContainer = document.getElementById('videojs-watermark-container');
            if (watermarkContainer) {
                if (player.isFullscreen()) {
                    // Force watermark to be visible in fullscreen
                    watermarkContainer.style.position = 'fixed';
                    watermarkContainer.style.top = '0';
                    watermarkContainer.style.left = '0';
                    watermarkContainer.style.right = '0';
                    watermarkContainer.style.bottom = '0';
                    watermarkContainer.style.width = '100vw';
                    watermarkContainer.style.height = '100vh';
                    watermarkContainer.style.zIndex = '2147483647';
                    watermarkContainer.style.pointerEvents = 'none';
                    watermarkContainer.style.display = 'block';
                } else {
                    // Reset watermark position when exiting fullscreen
                    watermarkContainer.style.position = 'absolute';
                    watermarkContainer.style.top = '0';
                    watermarkContainer.style.left = '0';
                    watermarkContainer.style.right = '0';
                    watermarkContainer.style.bottom = '0';
                    watermarkContainer.style.width = '';
                    watermarkContainer.style.height = '';
                    watermarkContainer.style.zIndex = '9999';
                    watermarkContainer.style.pointerEvents = 'none';
                    watermarkContainer.style.display = 'block';
                }
            }
        }
    };

    const onTimeupdate = (player) => () => {
        const currentTime = player.currentTime();
        const intTime = parseInt(currentTime);
        if (intTime % 15 == 0 && intTime > 0) {
            if (currentTime > observedTime.current + 1) {
                sendWatchToServer(intTime, episodeID);
                observedTime.current = intTime;
                onSendWatchToServer(intTime, episodeID);
            }
        }
    };

    const onLoadedmetadata = (player) => () => {
        player.currentTime(duration);
    };

    const cleanUp = () => {
        document.removeEventListener('click', closeMenus);
        playerRef.current?.dispose();
        seekBarRef.current?.dispose();
        currentTimeDisplayRef.current?.dispose();
        durationTimeDisplayRef.current?.dispose();
        playerRef.current = null;
    };

    const changeEpisode = (episode) => {
        if (playerRef.current) {
            playerRef.current.src(null);
            playerRef.current.reset();
            setTimeout(() => {
                playerRef.current.src({ src: episode.hls || episode.url });
                playerRef.current?.play();
            }, 50);
        }
    };

    const getVideoElement = () => {
        const videoElement = playerRef.current?.el()?.querySelector('video');
        return videoElement;
    };

    const videoPlayerRef = useRef(null);

    useEffect(() => {
        if (playerRef.current && videoPlayerRef.current === null) {
            const videoElement = getVideoElement();
            if (videoElement) {
                videoPlayerRef.current = videoElement;
            }
        }
    }, [playerRef.current]);

    const videoContent = (
        <div
            data-vjs-player
            style={{ direction: 'ltr', overflow: 'hidden' }}
            className={isFullHeight ? 'height-full' : ''}
        >
            <div ref={videoRef} className={isFullHeight ? 'height-full' : ''} />
        </div>
    );

    return enableProtection ? (
        <VideoProtection
            watermarkText=''
            videoRef={videoPlayerRef}
            pauseOnRecording={true}
            showWarning={true}
            enableDynamicWatermark={false}
        >
            {videoContent}
        </VideoProtection>
    ) : (
        videoContent
    );
};

export default FullVideoPlayer;

